import { createSlice } from "@reduxjs/toolkit";
import { DEFAULT_MIN_EXPLORE_SAMPLE_SIZE } from "utils/insight/insights";
import { chainComparators, getNumericComparator, getStringComparator, reverseOrder } from "utils/collect";
import { getCorpusList, getRawDatasets } from "containers/View/reducer";
import { getParentDatasetIds } from "utils/relation";
import { getAiReadyMetrics, getCorrelationsFromDataset } from "@/containers/Setup/reducer";
import { getCorpusDocumentList } from "@/containers/LanguageModelArtifact/reducer";
import { getAccountName } from "@/containers/Accounts/reducer";
import { fetchDataset, getDataset } from "@/containers/Home/reducer";
import { copyAndTransform } from "@/api/DatasetAPI";
export const DEFAULT_TILE_STATE = {
    isOpenAiReadiness: false,
    isOpenLineage: false,
    isOpenDataValidation: false,
    isOpenSettings: false,
    isOpenOutcome: false,
    isOpenInsights: false,
    isOpenDataEvaluated: false,
    isOpenTransformations: false,
    isOpenTargetModelOptions: false,
    isOpenKpiWhatsChangedOptions: false,
    isLoadingExplore: false,
    isLoadingCreateAI: false,
    isLoadingChat: false,
    isExpanded: false,
    minimumValueCount: DEFAULT_MIN_EXPLORE_SAMPLE_SIZE,
    dateAnalysis: {},
    outcome: { isLoadingRetarget: false }
};
const storedPinnedDatasets = localStorage?.getItem("sensePinnedDatasets");
const pinnedDatasets = storedPinnedDatasets === undefined || storedPinnedDatasets === null ? {} : JSON.parse(storedPinnedDatasets);
const initialState = {
    listExpandedView: false,
    listSortColumn: "dateCreated",
    listSortAscending: false,
    pinnedDatasets: pinnedDatasets,
    datasetViewType: localStorage && localStorage.getItem("dataset_default_view") === "TILE" ? "ALL" : "STRUCTURED",
    filterIntermediateDatasets: false,
    datasets: {}
};
const relationSlice = createSlice({
    name: "sense",
    initialState: initialState,
    reducers: {
        updateDataset(state, { payload }) {
            return {
                ...state,
                datasets: {
                    ...state.datasets,
                    [payload.id]: {
                        ...(state.datasets[payload.id] ?? DEFAULT_TILE_STATE),
                        ...payload.update
                    }
                }
            };
        },
        updateGlobal(state, { payload }) {
            return {
                ...state,
                ...payload.update
            };
        }
    }
});
const { updateDataset, updateGlobal } = relationSlice.actions;
export default relationSlice.reducer;
function getBase(state) {
    return state.sense;
}
export function getListSortColumn(state) {
    return getBase(state).listSortColumn;
}
export function getListSortAscending(state) {
    return getBase(state).listSortAscending;
}
export function getListExpandedView(state) {
    return getBase(state).listExpandedView;
}
export function getDatasetViewType(state) {
    return getBase(state).datasetViewType;
}
export function getFilterIntermediateDatasets(state) {
    return getBase(state).filterIntermediateDatasets;
}
export function getPinnedDatasets(state) {
    return getBase(state).pinnedDatasets;
}
function getPinnedId(id, type) {
    return type == "CORPUS" ? `CORPUS_${id}` : id;
}
export function getIsPinned(state, id, type) {
    return getPinnedDatasets(state)[getPinnedId(id, type)] !== undefined;
}
const COMPARATOR = chainComparators([
    reverseOrder(getNumericComparator((x) => Number(x.isPinned))),
    reverseOrder(getStringComparator((x) => x.created_date))
]);
reverseOrder(getStringComparator((x) => x.created_date));
export function getEntities(state) {
    const datasetViewType = getDatasetViewType(state);
    const datasets = ["STRUCTURED", "ALL"].includes(datasetViewType) ? getRawDatasets(state) : [];
    const documentSets = ["UNSTRUCTURED", "ALL"].includes(datasetViewType) ? getCorpusList(state) : [];
    const filterIntermediateDatasets = getFilterIntermediateDatasets(state);
    const intermediateDatasetIds = new Set();
    if (filterIntermediateDatasets) {
        datasets
            .flatMap((d) => {
            if (d.transformations.plan?.relation === undefined) {
                return [];
            }
            return getParentDatasetIds(d.transformations.plan?.relation);
        })
            .forEach((id) => intermediateDatasetIds.add(id));
    }
    return datasets
        .filter((d) => !intermediateDatasetIds.has(d.id))
        .map((d) => ({
        id: d.id,
        created_date: d.created_date,
        isPinned: getIsPinned(state, d.id, "DATASET"),
        type: "DATASET"
    }))
        .concat(documentSets.map((d) => ({
        id: d.id,
        created_date: d.created_date,
        isPinned: getIsPinned(state, d.id, "CORPUS"),
        type: "CORPUS"
    })))
        .sort(COMPARATOR)
        .map((e) => [e.id, e.type]);
}
export function getDatasetsStructuredTable(state) {
    const rawDatasets = getRawDatasets(state);
    return rawDatasets.map((dataset) => {
        const aiReadyMetrics = getAiReadyMetrics(state, "new", dataset.id);
        const predictors = [...(aiReadyMetrics?.aiReady ?? []), ...(aiReadyMetrics?.secondOrder?.aiReady ?? [])].sort((a, b) => b.value - a.value);
        const correlations = getCorrelationsFromDataset(state, undefined, dataset.id);
        return {
            ...dataset,
            isPinned: getIsPinned(state, dataset.id, "DATASET"),
            readiness: (aiReadyMetrics?.aiReadinessScore ?? 0) * 10,
            usefulVariables: predictors.length,
            problematicPredictors: correlations?.length ?? 0,
            source: getAccountName(state, dataset?.account_id),
            aiReadinessStatus: aiReadyMetrics?.aiReadinessStatus
        };
    });
}
export function getArtifactsUnstructuredTable(state) {
    const artifacts = getCorpusList(state) ?? [];
    return artifacts.map((artifact) => {
        const documentList = getCorpusDocumentList(state, artifact.id);
        const filteredDocs = documentList?.filter((doc) => {
            if (doc.startsWith("embedding") && doc.endsWith(".json")) {
                return false;
            }
            return true;
        });
        const cloudAccountName = getAccountName(state, artifact?.account_id);
        return {
            isPinned: getIsPinned(state, artifact.id, "CORPUS"),
            name: artifact.name,
            id: artifact.id,
            source: cloudAccountName,
            createdDate: artifact.created_date,
            owner: artifact.created_by.fullname,
            documentCount: filteredDocs?.length ?? 0,
            status: artifact?.metadata?.status
        };
    });
}
export function updateTileState(id, update) {
    return (dispatch) => {
        return dispatch(updateDataset({ id, update }));
    };
}
export function updateIsPinned(id, isPinned, type) {
    return (dispatch, getState) => {
        const pinnedId = getPinnedId(id, type);
        const pinnedDatasets = { ...getPinnedDatasets(getState()) };
        if (isPinned) {
            pinnedDatasets[pinnedId] = new Date().toJSON();
        }
        else {
            delete pinnedDatasets[pinnedId];
        }
        dispatch(updateGlobalState({ pinnedDatasets }));
    };
}
export function updateGlobalState(update) {
    return (dispatch) => {
        if (update.pinnedDatasets && localStorage) {
            localStorage.setItem("sensePinnedDatasets", JSON.stringify(update.pinnedDatasets));
        }
        return dispatch(updateGlobal({ update }));
    };
}
export function updateFilterIntermediateDatasets(filterIntermediateDatasets) {
    return (dispatch) => {
        dispatch(updateGlobalState({ filterIntermediateDatasets }));
    };
}
export function duplicateDatasetById(datasetId) {
    return (dispatch, getState) => {
        const dataset = getDataset(getState(), datasetId);
        if (!dataset) {
            return Promise.resolve(dataset);
        }
        return copyAndTransform(dataset).then((response) => {
            dispatch(fetchDataset(response.id));
        });
    };
}
